import { Activity, CSSProperties, useMemo, useRef } from 'react';
import classNames from 'classnames';

import styles from './chip-styles/chips.module.css';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { TChipProps } from './chips.types';
import getChipIconSize from './helpers/get-chip-icon-size';
import Text from '@ui/text/text/text';

{
  /*
		Why div's and span's instead of buttons?
		Design have "chips" that are optionally "selectable", "removeable" and/or used within lists/dropdowns/tabs.
		This makes using buttons difficult because nested buttons are poor form, and needing to tab through long lists is poor form.
		This lets us have maintain our nested state and keep accessability at the same time.
		Tabbing is still enabled by default when onClick is passed in, but can be easily bypassed via:
		- `removeProps={{ tabIndex: -1 }}`
		- `selectProps={{ tabIndex: -1 }}`
		and the data index attributes will let us write custom keyboard arrow cycling if we need to for lists
	*/
}
const Chip = ({
  type = 'info',
  size,
  removeProps,
  selectProps,
  avatar,
  icon,
  label,
  idx,
  value,
  disabled,
  shape = 'round',
  onClick,
  onRemove,
  selected,
  textProps,
  CustomRemoveIcon,
  noHover = false,
}: TChipProps) => {
  const removeRef = useRef<HTMLSpanElement | null>(null);

  const selectable = useMemo(() => {
    return !!onClick || (selectProps?.tabIndex ?? -1) >= 0;
  }, [onClick, selectProps?.tabIndex]);

  return (
    <div
      tabIndex={selectable ? 0 : -1} /* put before spread so we can override it */
      {...selectProps}
      /* intentionally putting all this after the props spread */
      role={onClick ? 'button' : undefined}
      className={classNames(
        selectProps?.className || '',
        styles.chip,
        styles[`size-${size}`],
        styles[type],
        styles[shape],
        {
          [styles.removable]: !!onRemove,
          [styles.selectable]: selectable,
          [styles.selected]: selected,
          [styles['filter-tab-selected']]: type === 'filter-tab' && selected,
          [styles['filter-tab-usual']]: type === 'filter-tab' && !selected,
          [styles['no-hover']]: !!noHover,
        }
      )}
      style={
        {
          ...(selectProps?.style || {}),
          '--chip-size': `${size}px`,
        } as CSSProperties
      }
      data-chip
      aria-disabled={disabled}
      inert={disabled}
      data-chip-idx={idx}
      data-chip-value={value}
      data-chip-selected={!!selected}
      onClick={(e) => {
        selectProps?.onClick?.(e);

        if (!selectable) return;
        const target = e.target && e.target instanceof Element ? e.target : null;
        if (removeRef.current?.contains(target)) {
          return;
        }
        e.preventDefault();
        onClick?.();
      }}
      onKeyDown={(e) => {
        selectProps?.onKeyDown?.(e);
        if (!selectable) return;
        const target = e.target && e.target instanceof Element ? e.target : null;
        if (removeRef.current?.contains(target)) {
          return;
        }
        if (['Enter', ' '].includes(e.key)) {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className={styles.label}>
        <Activity mode={avatar ? 'visible' : 'hidden'}>
          <div
            className={`${avatar ? styles.avatar : ''}`}
            style={{ '--chip-avatar-src': `url("${avatar}")` } as CSSProperties}
          />
        </Activity>

        <Activity mode={icon ? 'visible' : 'hidden'}>
          <Icon
            className={icon ? styles.icon : ''}
            name={icon || ''}
            size={getChipIconSize({
              type,
              size,
              shape,
            })}
          />
        </Activity>

        <Text {...textProps}>{label}</Text>
      </div>

      <Activity mode={type === 'input' && (onRemove || CustomRemoveIcon) ? 'visible' : 'hidden'}>
        {CustomRemoveIcon ? (
          CustomRemoveIcon
        ) : (
          <span
            tabIndex={0}
            {...removeProps}
            /* intentionally below spread */
            className={`${type === 'input' && onRemove ? styles['input-removable'] : ''}`}
            ref={removeRef}
            data-chip-remove=""
            data-chip-remove-idx={idx}
            data-chip-remove-value={value}
            aria-label={`Remove option ${label}`}
            role="button"
            onClick={(e) => {
              e.preventDefault();
              onRemove?.();
              removeProps?.onClick?.(e);
            }}
            onKeyDown={(e) => {
              if (['Enter', ' '].includes(e.key)) {
                e.preventDefault();
                onRemove?.();
              }
              removeProps?.onKeyDown?.(e);
            }}
          >
            <Icon name={ICONS.CLOSE} size={12} />
          </span>
        )}
      </Activity>
    </div>
  );
};

export default Chip;
